import { difficultyLabels } from '../game/difficultyLabel';
import type { ResultViewModel, TrackEntry } from '../game/result';
import type { LevelIntensity } from '../generation/curve';
import { resolveQuerySelector, setText } from './dom';

export interface ResultViewDeps {
  readonly reducedMotionQuery: MediaQueryList;
  readonly intensityForLevel: (levelNumber: number) => LevelIntensity;
  readonly getFallDistance: () => number;
  readonly onNext: () => void;
  readonly onPlayAgain: () => void;
}

export interface ResultViewApi {
  render(result: ResultViewModel): void;
  onShown(): void;
  onHidden(): void;
  disableActions(): void;
  restoreActions(isFinalLevel: boolean): void;
  destroy(): void;
}

export function createResultView(deps: ResultViewDeps): ResultViewApi {
  const querySelector = resolveQuerySelector();
  const resultCard = querySelector<HTMLElement>('.result-card');
  const resultLevel = querySelector<HTMLElement>('#result-level');
  const resultSubmessage = querySelector<HTMLElement>('#result-submessage');
  const resultDifficulty = querySelector<HTMLElement>('#result-difficulty');
  const resultTime = querySelector<HTMLElement>('#result-time');
  const resultArrows = querySelector<HTMLElement>('#result-arrows');
  const resultWins = querySelector<HTMLElement>('#result-wins');
  const resultStreakLabel = querySelector<HTMLElement>('#result-streak-label');
  const resultTrack = querySelector<HTMLElement>('#result-track');
  const resultTrackWrap = querySelector<HTMLElement>('.result-track-wrap');
  const resultConfetti = querySelector<HTMLElement>('#result-confetti');
  const nextLevelButton = querySelector<HTMLButtonElement>('#next-level-button');
  const playAgainButton = querySelector<HTMLButtonElement>('#play-again-button');

  const trackStateLabels: Readonly<Record<TrackEntry['state'], string>> = {
    current: '今クリアしたレベル',
    next: '次のレベル',
    upcoming: 'この先のレベル',
  };
  /*
   * 難易度は「この先の地形」としてチップの下に段組みの棒で出す。
   * 棒の本数がそのまま段位。
   */
  const trackGradeSteps: Readonly<Record<LevelIntensity, number>> = {
    normal: 1,
    hard: 2,
    superhard: 3,
  };
  const trackAdvanceDelayMs = 1150;
  const confettiTones = ['mint', 'amber', 'coral'] as const;
  let trackAdvanceTimer = 0;
  let confettiTimer = 0;

  const buildTrackChip = (entry: TrackEntry): HTMLLIElement => {
    const chip = document.createElement('li');
    chip.className =
      `result-chip result-chip--${entry.state} result-chip--grade-${entry.intensity}`;

    const dot = document.createElement('span');
    dot.className = 'result-chip__dot';
    dot.setAttribute('aria-hidden', 'true');
    dot.textContent = String(entry.levelNumber);

    const description = document.createElement('span');
    description.className = 'visually-hidden';
    const difficulty = difficultyLabels[entry.intensity];
    description.textContent =
      `レベル ${entry.levelNumber} ${difficulty} ${trackStateLabels[entry.state]}`;

    chip.append(dot, description);

    const steps = trackGradeSteps[entry.intensity];
    if (steps > 0) {
      const grade = document.createElement('span');
      grade.className = 'result-chip__grade';
      grade.setAttribute('aria-hidden', 'true');
      grade.append(...Array.from({ length: steps }, () => {
        const bar = document.createElement('span');
        bar.className = 'result-chip__grade-bar';
        return bar;
      }));
      chip.append(grade);
    }

    if (entry.state === 'current') {
      const stamp = document.createElement('span');
      stamp.className = 'result-chip__stamp';
      stamp.setAttribute('aria-hidden', 'true');
      chip.append(stamp);
    }
    return chip;
  };

  const cancelTrackAdvance = (): void => {
    window.clearTimeout(trackAdvanceTimer);
    trackAdvanceTimer = 0;
  };

  const renderTrack = (track: readonly TrackEntry[]): void => {
    if (resultTrack === null || resultTrackWrap === null) {
      return;
    }
    cancelTrackAdvance();
    resultTrack.replaceChildren(...track.map(buildTrackChip));
    // 最終レベルでは current の 1 枚だけになり、送り出す先がない。
    const advances = track.length > 1;
    const visibleSlots = track.length - (advances ? 1 : 0);
    resultTrackWrap.style.setProperty('--visible-slots', String(visibleSlots));
    resultTrackWrap.dataset.phase = !advances
      ? 'static'
      : deps.reducedMotionQuery.matches ? 'advanced' : 'intro';
  };

  const startTrackAdvance = (): void => {
    cancelTrackAdvance();
    if (resultTrackWrap === null || resultTrackWrap.dataset.phase !== 'intro') {
      return;
    }
    trackAdvanceTimer = window.setTimeout(() => {
      trackAdvanceTimer = 0;
      if (resultTrackWrap.dataset.phase === 'intro') {
        resultTrackWrap.dataset.phase = 'advanced';
      }
    }, trackAdvanceDelayMs);
  };

  const clearConfetti = (): void => {
    window.clearTimeout(confettiTimer);
    resultConfetti?.replaceChildren();
  };

  const launchConfetti = (): void => {
    if (resultConfetti === null || deps.reducedMotionQuery.matches) {
      return;
    }
    const fallDistance = deps.getFallDistance();
    const pieces = Array.from({ length: 26 }, (_, index) => {
      const piece = document.createElement('span');
      const tone = confettiTones[index % confettiTones.length] ?? confettiTones[0];
      piece.className = index % 3 === 0
        ? `confetti-piece confetti-piece--round confetti-piece--${tone}`
        : `confetti-piece confetti-piece--${tone}`;
      piece.style.left = `${Math.round(Math.random() * 96)}%`;
      piece.style.setProperty('--drift', `${Math.round((Math.random() - 0.5) * 90)}px`);
      piece.style.setProperty('--fall', `${fallDistance}px`);
      piece.style.setProperty('--spin', `${Math.round(220 + Math.random() * 560)}deg`);
      piece.style.animationDuration = `${(1.1 + Math.random() * 0.5).toFixed(2)}s`;
      piece.style.animationDelay = `${Math.round(Math.random() * 320)}ms`;
      return piece;
    });
    window.clearTimeout(confettiTimer);
    resultConfetti.replaceChildren(...pieces);
    confettiTimer = window.setTimeout(() => resultConfetti.replaceChildren(), 2000);
  };

  const disableActions = (): void => {
    if (nextLevelButton !== null) {
      nextLevelButton.disabled = true;
    }
    if (playAgainButton !== null) {
      playAgainButton.disabled = true;
    }
  };

  const restoreActions = (isFinalLevel: boolean): void => {
    if (nextLevelButton !== null) {
      nextLevelButton.disabled = isFinalLevel;
    }
    if (playAgainButton !== null) {
      playAgainButton.disabled = false;
    }
  };

  nextLevelButton?.addEventListener('click', deps.onNext);
  playAgainButton?.addEventListener('click', deps.onPlayAgain);

  return {
    render: (result) => {
      /*
       * カードは自前の data-difficulty を持つ。次レベル読み込み中も、表示中の
       * 結果カードはクリアしたレベルの色を保つ。
       */
      resultCard?.setAttribute('data-difficulty', deps.intensityForLevel(result.levelNumber));
      setText(resultLevel, `LEVEL ${String(result.levelNumber).padStart(2, '0')}`);
      setText(resultSubmessage, result.praiseText);
      setText(resultDifficulty, result.difficultyLabel);
      setText(resultTime, result.timeText);
      setText(resultArrows, `${result.arrowTotal} 本`);
      setText(resultWins, `×${result.cleanStreak}`);
      setText(resultStreakLabel, `ゲームオーバーなしの連続クリア ${result.cleanStreak} 回`);
      renderTrack(result.track);
      if (nextLevelButton !== null) {
        nextLevelButton.disabled = result.isFinalLevel;
        nextLevelButton.textContent = result.isFinalLevel ? '全レベル制覇！' : '次のレベル';
      }
      if (playAgainButton !== null) {
        playAgainButton.disabled = false;
      }
    },
    onShown: () => {
      launchConfetti();
      startTrackAdvance();
    },
    onHidden: () => {
      clearConfetti();
      cancelTrackAdvance();
    },
    disableActions,
    restoreActions,
    destroy: () => {
      clearConfetti();
      cancelTrackAdvance();
      nextLevelButton?.removeEventListener('click', deps.onNext);
      playAgainButton?.removeEventListener('click', deps.onPlayAgain);
    },
  };
}
